import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  PencilIcon,
  XMarkIcon,
  PlayIcon,
  PauseIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  UserIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const ActiveSubscriptions = ({ subscriptions = [] }) => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  // Suspend/Resume subscription mutation
  const suspendMutation = useMutation(
    ({ id, action }) => axios.patch(`/subscriptions/${id}/${action}`),
    {
      onSuccess: (data, variables) => {
        const actionText = variables.action === 'suspend' ? 'suspendu' : 'repris';
        toast.success(`Abonnement ${actionText} avec succès!`);
        queryClient.invalidateQueries('subscriptions');
      },
      onError: () => {
        toast.error('Erreur lors de l\'opération');
      },
    }
  );

  // Cancel subscription mutation
  const cancelMutation = useMutation(
    (id) => axios.delete(`/subscriptions/${id}`),
    {
      onSuccess: () => {
        toast.success('Abonnement annulé avec succès!');
        queryClient.invalidateQueries('subscriptions');
      },
      onError: () => {
        toast.error('Erreur lors de l\'annulation');
      },
    }
  );

  // Filter subscriptions
  const filteredSubscriptions = subscriptions.filter(subscription => {
    const matchesSearch = subscription.memberName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         subscription.planName?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || subscription.status === statusFilter;
    const matchesType = typeFilter === 'all' || subscription.type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  const handleSuspendResume = (id, currentStatus) => {
    const action = currentStatus === 'active' ? 'suspend' : 'resume';
    const confirmText = action === 'suspend' ? 'suspendre' : 'reprendre';
    
    if (window.confirm(`Êtes-vous sûr de vouloir ${confirmText} cet abonnement ?`)) {
      suspendMutation.mutate({ id, action });
    }
  };

  const handleCancel = (id, memberName) => {
    if (window.confirm(`Êtes-vous sûr de vouloir annuler l'abonnement de ${memberName} ?`)) {
      cancelMutation.mutate(id);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/20 text-green-400';
      case 'suspended':
        return 'bg-yellow-500/20 text-yellow-400';
      case 'expired':
        return 'bg-red-500/20 text-red-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getStatusText = (status) => {
    const statusTexts = {
      active: 'Actif',
      suspended: 'Suspendu',
      expired: 'Expiré',
    };
    return statusTexts[status] || status;
  };

  const getDaysUntilExpiry = (endDate) => {
    const days = Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24));
    return days;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Search and Filters */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
          <div className="relative flex-1 max-w-md">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
            <input
              type="text"
              placeholder="Rechercher membre ou plan..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div className="flex items-center space-x-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actifs</option>
              <option value="suspended">Suspendus</option>
              <option value="expired">Expirés</option>
            </select>
            
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="bg-white/10 border border-white/20 rounded-xl px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous les types</option>
              <option value="daily">Quotidien</option>
              <option value="weekly">Hebdomadaire</option>
              <option value="monthly">Mensuel</option>
              <option value="quarterly">Trimestriel</option>
              <option value="annual">Annuel</option>
            </select>
          </div>
        </div>
      </div>

      {/* Subscriptions List */}
      <div className="space-y-4">
        {filteredSubscriptions.length > 0 ? (
          filteredSubscriptions.map((subscription, index) => {
            const daysUntilExpiry = getDaysUntilExpiry(subscription.endDate);
            const isExpiringSoon = daysUntilExpiry <= 7 && daysUntilExpiry > 0;
            
            return (
              <motion.div
                key={subscription.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`bg-white/10 backdrop-blur-lg rounded-3xl p-6 border transition-all duration-300 hover:bg-white/15 ${
                  isExpiringSoon ? 'border-orange-500/50' : 'border-white/20'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center">
                      <UserIcon className="h-6 w-6 text-white" />
                    </div>
                    
                    <div>
                      <h3 className="text-lg font-semibold text-white">{subscription.memberName}</h3>
                      <p className="text-white/60 text-sm">{subscription.planName}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-6">
                    {/* Price */}
                    <div className="text-right">
                      <p className="text-white font-semibold">{subscription.price?.toLocaleString()} DA</p>
                      <p className="text-white/60 text-sm">Prix payé</p>
                    </div>
                    
                    {/* Dates */}
                    <div className="text-right">
                      <div className="flex items-center space-x-2">
                        <CalendarDaysIcon className="h-4 w-4 text-white/50" />
                        <span className="text-white text-sm">
                          {new Date(subscription.endDate).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                      <p className={`text-sm font-medium ${
                        daysUntilExpiry <= 0 ? 'text-red-400' : 
                        daysUntilExpiry <= 7 ? 'text-orange-400' : 'text-white/60'
                      }`}>
                        {daysUntilExpiry <= 0 ? 'Expiré' : `${daysUntilExpiry} jours restants`}
                      </p>
                    </div>
                    
                    {/* Status */}
                    <div className="text-center">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(subscription.status)}`}>
                        {getStatusText(subscription.status)}
                      </span>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex items-center space-x-2">
                      <Link
                        to={`/subscriptions/${subscription.id}/edit`}
                        className="p-2 bg-blue-500/20 text-blue-400 rounded-xl hover:bg-blue-500/30 transition-colors"
                        title="Modifier"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </Link>
                      
                      <button
                        onClick={() => handleSuspendResume(subscription.id, subscription.status)}
                        disabled={suspendMutation.isLoading}
                        className={`p-2 rounded-xl transition-colors disabled:opacity-50 ${
                          subscription.status === 'active' 
                            ? 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30' 
                            : 'bg-green-500/20 text-green-400 hover:bg-green-500/30'
                        }`}
                        title={subscription.status === 'active' ? 'Suspendre' : 'Reprendre'}
                      >
                        {subscription.status === 'active' ? (
                          <PauseIcon className="h-4 w-4" />
                        ) : (
                          <PlayIcon className="h-4 w-4" />
                        )}
                      </button>
                      
                      <button
                        onClick={() => handleCancel(subscription.id, subscription.memberName)}
                        disabled={cancelMutation.isLoading}
                        className="p-2 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30 transition-colors disabled:opacity-50"
                        title="Annuler"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Expiry Warning */}
                {isExpiringSoon && (
                  <div className="mt-4 p-3 bg-orange-500/20 rounded-xl border border-orange-500/30">
                    <div className="flex items-center space-x-2">
                      <ExclamationTriangleIcon className="h-5 w-5 text-orange-400" />
                      <p className="text-orange-400 text-sm font-medium">
                        Abonnement expire dans {daysUntilExpiry} jour{daysUntilExpiry > 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })
        ) : (
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-12 border border-white/20 text-center">
            <UserIcon className="h-16 w-16 text-white/30 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              Aucun abonnement trouvé
            </h3>
            <p className="text-white/60 mb-6">
              {searchTerm || statusFilter !== 'all' || typeFilter !== 'all'
                ? 'Aucun abonnement ne correspond à vos critères de recherche.'
                : 'Aucun abonnement actif pour le moment.'}
            </p>
            <Link
              to="/subscriptions/new"
              className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 inline-block"
            >
              Créer un Abonnement
            </Link>
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default ActiveSubscriptions; 